import { useAppSelector } from "@app/hooks";
import { LBtn, LFlex, LInputField } from "@components";
import { convertToSentenceCase } from "@helpers/stringHelper";
import { useUserPermission } from "@helpers/userPermissionHelper";
import { Note } from "@store/generalTypes";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Translate } from "react-i18nify";
import { ExistingNotes } from "./ExistingNotes";

export const NotesContainer = ({
    notes,
    showNoteDetail,
    allowAddNote,
    displayAddButtonOnHover,
    onCreateNote
}: NotesContainerProps): JSX.Element => {
    const [editing, setEditing] = useState(false);
    const [adding, setAdding] = useState(false);
    const [saveDisabled, setSaveDisabled] = useState(true);

    const validateMethods = useForm();
    const me = useAppSelector((x) => x.userDetail);
    const { userRoles } = useUserPermission({ useCurrentLocation: true });

    const toggleEditing = (): void => {
        validateMethods.resetField("note");
        setSaveDisabled(true);
        setEditing(!editing);
    };

    const handleSaveNote = async (): Promise<void> => {
        const newNote: Note = {
            firstName: me.firstName,
            lastName: me.lastName,
            roles: userRoles,
            text: convertToSentenceCase(validateMethods.getValues("note") ?? ""),
            createdDate: new Date().toISOString()
        };

        await onCreateNote?.(newNote);

        setEditing(false);
        setAdding(false);
    };

    useEffect(() => {
        const subscription = validateMethods.watch((value) => {
            setSaveDisabled(value?.note?.length < 6);
        });
        return () => subscription.unsubscribe();
    }, [validateMethods.watch]);

    const renderAddButton = (): JSX.Element => {
        if (!allowAddNote) {
            return <></>;
        }

        return displayAddButtonOnHover ? (
            <a className="has-text-weight-bold is-size-7 present-target-actions" onClick={toggleEditing}>
                <Translate value="admin.addNote" />
                {" +"}
            </a>
        ) : (
            <LBtn classes="mt-4" onClick={toggleEditing} smallSpacing variation="outline" isBold>
                <p className="mx-4">
                    <Translate value="admin.addNote" />
                </p>
            </LBtn>
        );
    };

    if (editing) {
        return (
            <>
                <ExistingNotes notes={notes!} showDetail={showNoteDetail} />
                <FormProvider {...validateMethods}>
                    <LFlex className="mt-4 mb-4" row alignCenter justifyCenter>
                        <div style={{ width: "100%" }}>
                            <LInputField inputName="note" hideErrorLabel />
                        </div>
                        <LBtn
                            smallSpacing
                            smallTopBottomMargins
                            classes="py-3 px-1 ml-3"
                            onClick={() => {
                                setAdding(true);
                                void handleSaveNote();
                            }}
                            disabled={saveDisabled}
                            loading={adding}
                        >
                            <Translate value="admin.save" />
                        </LBtn>
                        <LBtn
                            variation="unstyled"
                            smallSpacing
                            smallTopBottomMargins
                            onClick={toggleEditing}
                            disabled={adding}
                        >
                            <Translate value="admin.cancel" />
                        </LBtn>
                    </LFlex>
                </FormProvider>
            </>
        );
    }

    return (
        <>
            <ExistingNotes notes={notes!} showDetail={showNoteDetail} />
            {renderAddButton()}
        </>
    );
};

interface NotesContainerProps {
    notes?: Note[];
    showNoteDetail?: boolean;
    allowAddNote?: boolean;
    displayAddButtonOnHover?: boolean;
    onCreateNote?: (note: Note) => Promise<void>;
}
